import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, FadeIn, Logo, GradientBackground } from '../components/ui';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import ProLogin from './ProLogin';

// Prominent, gently pulsing role-switch pill (top-right).
function RolePill({ isStaff, onClick }) {
  return (
    <button
      onClick={onClick}
      style={{
        position: 'absolute', top: 'calc(var(--sat) + 16px)', right: 16, zIndex: 10,
        display: 'flex', alignItems: 'center', gap: 8,
        background: '#fff', padding: '7px 6px 7px 14px', borderRadius: 'var(--r-pill)',
        boxShadow: '0 5px 10px rgba(0,0,0,0.25)', animation: 'float-y 1.8s ease-in-out infinite',
      }}
    >
      <span style={{ fontSize: 15 }}>{isStaff ? '👤' : '🧰'}</span>
      <span style={{ color: 'var(--primary)', fontWeight: 800, fontSize: 13.5 }}>
        {isStaff ? "I'm a User" : 'Get a Job'}
      </span>
      <span style={{
        width: 22, height: 22, borderRadius: 11, background: 'var(--primary)', color: '#fff',
        display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 900,
      }}>→</span>
    </button>
  );
}

const glassInput = {
  flex: 1, minWidth: 0, background: 'transparent', border: 'none', outline: 'none',
  padding: '15px 0', fontSize: 17, color: '#fff', letterSpacing: 1,
};
const inputRow = {
  display: 'flex', alignItems: 'center',
  background: 'rgba(255,255,255,0.14)', border: '1px solid rgba(255,255,255,0.26)',
  borderRadius: 'var(--r-md)', padding: '0 16px',
};

export default function Login() {
  const { requestOtp, verifyOtp } = useAuth();
  const { toast } = useToast();
  const [role, setRole] = useState('customer');
  const [step, setStep] = useState('mobile');
  const [mobile, setMobile] = useState('');
  const [otp, setOtp] = useState('');
  const [referral, setReferral] = useState('');
  const [loading, setLoading] = useState(false);

  const isStaff = role === 'staff';

  // Service-provider sign-in is a fully distinct experience so users never
  // confuse it with the customer login.
  if (isStaff) {
    return <ProLogin onBackToCustomer={() => { setRole('customer'); setStep('mobile'); }} />;
  }

  const sendOtp = async (e) => {
    e?.preventDefault();
    if (mobile.length !== 10) return toast('Enter a valid 10-digit number', { tone: 'error' });
    setLoading(true);
    try {
      const res = await requestOtp(mobile, role, referral.trim().toUpperCase() || undefined);
      if (res?.devOtp) setOtp(res.devOtp);
      setStep('otp');
    } catch (err) {
      toast(err.message || 'Please check your connection and try again.', { title: 'Could not send OTP', tone: 'error' });
    }
    setLoading(false);
  };

  const verify = async (e) => {
    e?.preventDefault();
    if (otp.length < 4) return toast('Enter the OTP', { tone: 'error' });
    setLoading(true);
    try {
      await verifyOtp(mobile, otp);
    } catch (err) {
      toast(err.message || 'Incorrect OTP or server unreachable.', { title: 'Login failed', tone: 'error' });
    }
    setLoading(false);
  };

  const switchRole = () => { setRole('staff'); setStep('mobile'); };

  return (
    <GradientBackground>
      {/* Never trap someone on the login screen — let them back to the site. */}
      <Link to="/" style={{
        position: 'absolute', top: 'calc(var(--sat) + 16px)', left: 16, zIndex: 10,
        background: 'rgba(0,0,0,0.18)', border: '1px solid rgba(255,255,255,0.25)',
        borderRadius: 'var(--r-pill)', padding: '8px 14px',
        color: '#fff', fontSize: 12.5, fontWeight: 700,
      }}>
        ← Home
      </Link>
      <RolePill isStaff={isStaff} onClick={switchRole} />

      <div style={{
        flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center',
        padding: 'calc(var(--sat) + 60px) 22px calc(var(--sab) + 30px)',
        maxWidth: 520, margin: '0 auto', width: '100%',
      }}>
        <FadeIn from={-10} style={{ display: 'flex', justifyContent: 'center', position: 'relative', marginBottom: 26 }}>
          <span style={{
            position: 'absolute', width: 190, height: 190, borderRadius: '50%', background: '#fff',
            opacity: 0.22, filter: 'blur(26px)', pointerEvents: 'none',
            animation: 'pulse-halo 3.6s ease-in-out infinite', top: '50%', left: '50%',
            marginTop: -95, marginLeft: -95,
          }} />
          <span style={{ position: 'relative' }}><Logo size="lg" /></span>
        </FadeIn>

        <FadeIn delay={120} style={{ textAlign: 'center' }}>
          <h1 style={{ color: '#fff', fontSize: 27, fontWeight: 800, margin: 0 }}>Trusted help, at home</h1>
          <p style={{ color: 'rgba(255,255,255,0.85)', fontSize: 14.5, marginTop: 8, lineHeight: 1.45 }}>
            Hire verified maids, cooks &amp; caretakers in minutes
          </p>
          <button onClick={switchRole} style={{
            marginTop: 14, background: 'rgba(0,0,0,0.16)', borderRadius: 'var(--r-pill)',
            padding: '8px 14px', color: 'rgba(255,255,255,0.85)', fontSize: 12.5,
          }}>
            Want to work as a maid, cook or helper?{' '}
            <span style={{ color: '#fff', fontWeight: 800, textDecoration: 'underline' }}>Get a Job ↗</span>
          </button>
        </FadeIn>

        <FadeIn delay={220} style={{
          marginTop: 26, background: 'rgba(255,255,255,0.12)',
          border: '1px solid rgba(255,255,255,0.22)', borderRadius: 26, padding: 22,
          boxShadow: '0 14px 24px rgba(0,0,0,0.2)',
        }}>
          <div style={{ color: '#fff', fontSize: 19, fontWeight: 800 }}>Login / Sign-up</div>
          <div style={{ color: 'rgba(255,255,255,0.75)', fontSize: 13, marginTop: 5, marginBottom: 18 }}>
            {step === 'mobile' ? 'Verify your number via WhatsApp OTP' : `Enter the OTP sent to +91 ${mobile}`}
          </div>

          {step === 'mobile' ? (
            <form onSubmit={sendOtp}>
              <div style={inputRow}>
                <span style={{ color: '#fff', fontSize: 15.5, fontWeight: 700 }}>🇮🇳 +91</span>
                <span style={{ width: 1, height: 22, background: 'rgba(255,255,255,0.3)', margin: '0 12px' }} />
                <input
                  style={glassInput} placeholder="00000 00000" inputMode="numeric" autoComplete="tel"
                  maxLength={10} value={mobile}
                  onChange={(e) => setMobile(e.target.value.replace(/\D/g, ''))}
                />
              </div>
              <div style={{ ...inputRow, marginTop: 12 }}>
                <span style={{ color: '#fff', fontSize: 15.5 }}>🎁</span>
                <span style={{ width: 1, height: 22, background: 'rgba(255,255,255,0.3)', margin: '0 12px' }} />
                <input
                  style={glassInput} placeholder="Referral code (optional)" maxLength={10} value={referral}
                  onChange={(e) => setReferral(e.target.value.replace(/[^A-Za-z0-9]/g, '').toUpperCase())}
                />
              </div>
              <Button type="submit" variant="light" disabled={loading} style={{ marginTop: 18 }}
                title={loading ? 'Sending…' : 'Continue  →'} />
            </form>
          ) : (
            <form onSubmit={verify}>
              <input
                style={{
                  width: '100%', background: 'rgba(255,255,255,0.14)',
                  border: '1px solid rgba(255,255,255,0.26)', borderRadius: 'var(--r-md)',
                  padding: '16px 0', fontSize: 22, color: '#fff', textAlign: 'center',
                  letterSpacing: 10, fontWeight: 700, outline: 'none',
                }}
                placeholder="••••••" inputMode="numeric" autoComplete="one-time-code"
                maxLength={6} value={otp} autoFocus
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
              />
              <Button type="submit" variant="light" disabled={loading} style={{ marginTop: 18 }}
                title={loading ? 'Verifying…' : 'Verify & Continue'} />
              <button type="button" onClick={() => setStep('mobile')}
                style={{ marginTop: 16, width: '100%', color: '#fff', fontWeight: 700, opacity: 0.9 }}>
                ← Change number
              </button>
            </form>
          )}
        </FadeIn>

        <FadeIn delay={320} style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 8, marginTop: 24 }}>
          {['🛡️ KYC Verified', '⚡ Instant Booking', '💬 WhatsApp OTP'].map((t) => (
            <span key={t} style={{
              background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.18)',
              padding: '7px 12px', borderRadius: 'var(--r-pill)',
              color: 'rgba(255,255,255,0.92)', fontSize: 12, fontWeight: 600,
            }}>{t}</span>
          ))}
        </FadeIn>

        <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: 11.5, textAlign: 'center', marginTop: 22 }}>
          By continuing you agree to our Terms &amp; Privacy Policy.
        </p>
      </div>
    </GradientBackground>
  );
}
